import { useId } from "react";
import { useTranslation } from "react-i18next";

import { Input, Label } from "~/components/ui";
import { cn } from "~/lib/utils";

interface GdtfDateInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Field-level error; replaces the GDTF hint when set. */
  error?: string;
  className?: string;
}

/** Single GDTF date field: label, the GDTF placeholder, and the "BC → leading hyphen" hint. */
export function GdtfDateInput({ label, value, onChange, error, className }: GdtfDateInputProps) {
  const { t } = useTranslation();
  const id = useId();

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label htmlFor={id} className="text-12-bold capitalize text-fg-2">
        {label}
      </Label>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t("ui.gdtfPlaceholder")}
        aria-invalid={error ? true : undefined}
      />
      {error ? (
        <p className="text-12-medium text-danger">{error}</p>
      ) : (
        <p className="text-12-regular text-fg-3">{t("ui.gdtfHint")}</p>
      )}
    </div>
  );
}

interface GdtfDateRangeInputProps {
  label: string;
  from: string;
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  className?: string;
}

/** GDTF date range: two fields separated by `~`, sharing one label and the GDTF hint. */
export function GdtfDateRangeInput({ label, from, to, onFromChange, onToChange, className }: GdtfDateRangeInputProps) {
  const { t } = useTranslation();

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label className="text-12-bold capitalize text-fg-2">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          value={from}
          onChange={(e) => onFromChange(e.target.value)}
          placeholder={t("ui.gdtfPlaceholder")}
          className="min-w-0 flex-1"
        />
        <span className="text-fg-3">~</span>
        <Input
          value={to}
          onChange={(e) => onToChange(e.target.value)}
          placeholder={t("ui.gdtfPlaceholder")}
          className="min-w-0 flex-1"
        />
      </div>
      <p className="text-12-regular text-fg-3">{t("ui.gdtfHint")}</p>
    </div>
  );
}
